/**
 * 메뉴 관리 — 콘솔 목록 공통 규격 채택본 (2026-09-09 사용자 지시).
 *
 * 규칙 다섯을 한 화면에 다 쓰는 첫 자리다:
 *   ① 검색·필터·등록은 목록 **위**            ② 분류(위치 4)가 있으므로 **좌 240 / 우 목록**
 *   ③ 수정은 **줄 끝 단추**                    ④ 순서는 번호로 보여 주고 **끌어서** 바꾼다
 *   ⑤ 줄을 누르면 **팝업**에서 고친다 (숫자로 고치는 칸은 그 팝업에만 있다)
 *
 * ⚠ 이전 화면 `MenuRegistrySection`(카드 격자)은 **지우지 않았다** — 되돌리기 대비다.
 *   편집 폼(`MenuModal`)은 그 파일 것을 그대로 가져다 쓴다(두 벌로 만들지 않는다).
 *
 * ⚠ 저장은 **PATCH 를 줄마다** 보낸다 — 일괄 재정렬 라우트를 새로 만들지 않았다.
 *   서버가 함께 배포돼야 도는 기능을 만들면 main 이 배포 기준선에서 내려온다(CLAUDE.md).
 *   메뉴는 열몇 줄이라 왕복이 문제되지 않는다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2, Eye, EyeOff } from '../../../icons';
import {
  listMenus, updateMenu, deleteMenu, type MenuRow,
} from '../../../api/menus';
import { RenderIcon } from './MenuControls';
import { useCustomIcons } from './iconStore';
import { MenuModal } from './MenuRegistrySection';
import { ConsoleListScreen } from '../../../components/console-list/ConsoleListScreen';
import { ConsoleListTable } from '../../../components/console-list/ConsoleListTable';

/** 좌 분류 = 메뉴가 서는 «위치». 서버의 section 값과 1:1. */
const SECTIONS = [
  { key: 'primary', label: '레일 상단' },
  { key: 'secondary', label: '레일 하단' },
  { key: 'status', label: '하단 상태바' },
  { key: 'aux', label: '보조영역' },
  // 2026-09-15 R6-12 — 모바일 앱에만 뜨는 메뉴(웹에는 안 뜬다).
  { key: 'mobile', label: '앱 전용' },
] as const;

/** 서버와 같은 차례로 세운다 — sort_order ASC, id ASC. */
function bySort(a: MenuRow, b: MenuRow): number {
  return a.sort_order - b.sort_order || a.id - b.id;
}

export function MenuListSection() {
  const [list, setList] = useState<MenuRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState<string>('primary');
  const [q, setQ] = useState('');
  const [onlyOn, setOnlyOn] = useState<'all' | 'on'>('all');
  const [editing, setEditing] = useState<MenuRow | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { customSvg, customNames } = useCustomIcons();

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setList(await listMenus()); }
    catch (e: any) { setError(e?.response?.data?.error ?? e?.message ?? '메뉴 목록 불러오기 실패'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  /** 고른 위치의 줄 — 언제나 서버 차례대로. 순서를 끌어 바꾸는 대상이 이것이다. */
  const inSection = useMemo(
    () => list.filter((m) => m.section === section).sort(bySort),
    [list, section],
  );
  /** 화면에 그릴 줄 — 검색·필터까지 걸린 것. */
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return inSection.filter((m) => {
      if (onlyOn === 'on' && !m.enabled) return false;
      if (!needle) return true;
      return `${m.label} ${m.url} ${m.menu_key}`.toLowerCase().includes(needle);
    });
  }, [inSection, q, onlyOn]);

  /**
   * ★ 걸러 놓고 끌면 자리가 틀린다 — 보이는 3번째가 실제 7번째일 수 있다.
   *   그래서 검색·필터가 걸려 있는 동안에는 손잡이를 잠그고 왜 잠갔는지를 적는다
   *   (정본 P3: 「할 수 없는 것은 잠근다 — 감추지 않는다」).
   */
  const filtered = q.trim() !== '' || onlyOn !== 'all';

  async function reorder(from: number, to: number) {
    const next = inSection.slice();
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    // 10 단위로 다시 매긴다 — 사이에 끼워 넣을 자리를 남겨 둔다.
    const changed = next
      .map((m, i) => ({ row: m, order: (i + 1) * 10 }))
      .filter(({ row, order }) => row.sort_order !== order);
    // 화면은 먼저 바뀐다(끌자마자 제자리에 놓인다). 실패하면 되읽어 되돌린다.
    setList((arr) => arr.map((m) => {
      const hit = changed.find((c) => c.row.id === m.id);
      return hit ? { ...m, sort_order: hit.order } : m;
    }));
    if (changed.length === 0) return;
    setSaving(true); setError(null);
    try {
      for (const c of changed) await updateMenu(c.row.id, { sort_order: c.order });
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? '순서 저장 실패 — 되돌립니다.');
      await reload();
    } finally { setSaving(false); }
  }

  async function toggle(m: MenuRow) {
    setBusyId(m.id);
    try {
      const u = await updateMenu(m.id, { enabled: !m.enabled });
      setList((arr) => arr.map((x) => (x.id === m.id ? u : x)));
    } catch (e: any) { setError(e?.response?.data?.error ?? '변경 실패'); }
    finally { setBusyId(null); }
  }

  async function remove(m: MenuRow) {
    if (!window.confirm(`메뉴 "${m.label}"(${m.url})를 삭제할까요?`)) return;
    setBusyId(m.id);
    try { await deleteMenu(m.id); await reload(); }
    catch (e: any) { setError(e?.response?.data?.error ?? '삭제 실패'); }
    finally { setBusyId(null); }
  }

  function open(m: MenuRow | null) { setEditing(m); setShowModal(true); }

  const enabledCount = list.filter((m) => m.enabled).length;

  return (
    <>
      <ConsoleListScreen
        title="메뉴 관리"
        kpi={`${list.length}개 · 노출 ${enabledCount}`}
        desc="사용자 사이트 메뉴(URL·라벨·아이콘·노출·모듈매핑) — 끄면 사이드바·상태바에서 숨기고 데이터 조회도 차단합니다."
        search={{ value: q, onChange: setQ, placeholder: '메뉴 이름 · 주소로 찾기' }}
        filters={{
          active: onlyOn,
          onChange: (k) => setOnlyOn(k as 'all' | 'on'),
          items: [
            { key: 'all', label: '전체', count: inSection.length },
            { key: 'on', label: '노출만', count: inSection.filter((m) => m.enabled).length },
          ],
        }}
        add={{ label: '메뉴 등록', onClick: () => open(null) }}
        side={{
          head: '위치',
          active: section,
          onChange: setSection,
          items: SECTIONS.map((s) => ({
            key: s.key,
            label: s.label,
            count: list.filter((m) => m.section === s.key).length,
          })),
        }}
        notice={error
          ? <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>
          : filtered
            ? <div className="admin-console__alert" role="status">
                검색·필터가 걸려 있는 동안에는 순서를 바꿀 수 없습니다 — 보이는 차례가 실제 차례와 다르기 때문입니다.
              </div>
            : null}
      >
        {loading ? (
          <div className="cl-empty">불러오는 중…</div>
        ) : (
          <ConsoleListTable
            rows={shown}
            rowKey={(m) => m.id}
            onRowClick={(m) => open(m)}
            ordering={{ onReorder: reorder, disabled: filtered || saving }}
            empty={inSection.length === 0
              ? <>이 위치에 등록된 메뉴가 없습니다.<span className="cl-empty__sub">위의 「메뉴 등록」으로 만듭니다.</span></>
              : <>찾는 메뉴가 없습니다.<span className="cl-empty__sub">검색어나 필터를 지워 보세요.</span></>}
            columns={[
              {
                key: 'name', header: '메뉴',
                render: (m) => (
                  <span className="cl-name">
                    <span className="cl-name__ico"><RenderIcon name={m.icon} size={16} customSvg={customSvg} /></span>
                    <span className="cl-name__t">{m.label}</span>
                    <span className="cl-name__s">{m.url}</span>
                  </span>
                ),
              },
              { key: 'key', header: '키', render: (m) => <span className="cl-name__s">{m.menu_key}</span> },
              { key: 'module', header: '모듈', render: (m) => m.module_key ?? '—' },
              {
                key: 'enabled', header: '노출',
                render: (m) => (
                  <span className={`cl-badge${m.enabled ? ' is-on' : ''}`}>{m.enabled ? '노출' : '숨김'}</span>
                ),
              },
            ]}
            /* 줄 끝 작업은 **아이콘만** 세운다 — 뜻은 aria-label·title 이 진다. */
            rowActions={(m) => (
              <>
                <button
                  type="button" className="cl-rowbtn"
                  aria-label={`${m.label} 수정`} title="수정"
                  onClick={() => open(m)}
                >
                  <Pencil size={13} />
                </button>
                <button
                  type="button" className="cl-rowbtn" disabled={busyId === m.id}
                  aria-label={m.enabled ? `${m.label} 숨기기` : `${m.label} 표시`}
                  title={m.enabled ? '숨기기' : '표시'}
                  onClick={() => void toggle(m)}
                >
                  {m.enabled ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
                <button
                  type="button" className="cl-rowbtn cl-rowbtn--danger" disabled={busyId === m.id}
                  aria-label={`${m.label} 삭제`} title="삭제" onClick={() => void remove(m)}
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
          />
        )}
      </ConsoleListScreen>

      {showModal && (
        <MenuModal
          existing={editing}
          customSvg={customSvg}
          customNames={customNames}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); void reload(); }}
        />
      )}
    </>
  );
}
